import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {Base} from "../../../../../../../../common/interfaces/base.interfaces";
import {ConfirmComponent} from "../../../../../../../../common/components/confirm/confirm.component";
import {Warehouse} from "../../../../../../../../common/interfaces/warehouse.interface";
import {ToastService} from "../../../../../../../../common/services/toast.service";
import {SpinnerService} from "../../../../../../../../common/services/spinner.service";
import {Api3Service} from "../../../../../../../../common/services/api3.service";
import {Table2Component} from "../../../../../../../../common/components/table2";
import {Table} from "../../../../../../../../common/interfaces/table.interface";
import {Modal, ModalService} from "../../../../../../../services/modal.service";
import {CommonFormComponent} from "../../../../../../../../common/components/form";
import {Form} from "../../../../../../../../common/interfaces/form.interface";
import IBox = Warehouse.IBox;
import ISelectOption = Form.ISelectOption;

@Component({
    selector: "section-warehouse-order-wizard-shuffle-boxes",
    template: `
        <common-table2 *ngIf="tableSettings" [settings]="tableSettings" (onRowsSelected)="selectedRows = $event">
            <div row1>
                <button mat-raised-button type="button" [disabled]="!selectedRows.length" color="accent"
                        (click)="linkSelected()">
                    Link selected
                </button>
            </div>
        </common-table2>
        <common-confirm></common-confirm>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class WarehouseProceduresWizardShuffleBoxesComponent implements OnInit, OnDestroy, OnChanges {

    protected destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public selectedRows: any[] = [];

    public tableSettings: Table.ISettings;

    @ViewChild(ConfirmComponent, {static: true})
    public confirmRef: ConfirmComponent;

    @ViewChild(Table2Component, {static: false})
    public tableRef: Table2Component;

    @Input()
    public shipment_id: number;

    @Input()
    public state: Base.IState;

    @Input()
    public packages: Warehouse.IPackage[];

    @Input()
    public selected_package_name: string = null;

    public constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        private api3Service: Api3Service,
        private toastService: ToastService,
        private spinnerService: SpinnerService,
        private modalService: ModalService,
    ) {
    }

    private url(): Table.ITableApi {
        return {
            url: [this.state.section, "shipments", "" + this.shipment_id, "transactions", "boxes"],
            query: {
                relations: [
                    "Pallet:id,ref",
                    "WarehousePackage:id,name"
                ],
                with_transaction_qty: true,
                with_transaction_package: true,
                warehouse_package: this.selected_package_name ?? null,
            },
            version: 3
        };
    }

    private prepareTable(): void {
        this.tableSettings = {
            api: this.url(),
            columns: [
                {
                    data: "warehouse_package.name",
                    name: "WarehousePackage.name",
                    title: "Package",
                    sortable: false,
                    searchable: false,
                    render: (row: any): string => {
                        return row.warehouse_package?.name ?? "NOT LINKED";
                    },
                    cssClassFn: (row: any): string => {
                        return !row.warehouse_package ? "text-error" : "";
                    }
                },
                {
                    data: "ref",
                    title: "Box ref"
                },
                {
                    data: "first_warehouse_transaction.item",
                    title: "Item",
                    sortable: false,
                },
                {
                    data: "warehouse_transactions_sum_quantity",
                    title: "Quantity",
                    sortable: false,
                    searchable: false,
                },
                {
                    data: "first_warehouse_transaction.ref",
                    title: "Order",
                },
                {
                    data: "pallet.ref",
                    name: "Pallet.ref",
                    title: "Pallet",
                    sortable: false,
                }
            ],
            actions: [
                {
                    name: "link",
                    title: "Link",
                    click: async (row: any): Promise<any> => {
                        await this.linkBox(row);
                        this.toastService.show("Box updated", "success");
                        this.tableRef.reload();
                    },
                },
            ],
            multi_select: true
        };
    }

    private async selectPackage(): Promise<string> {
        const res: Modal.IResponse = await this.modalService.open(CommonFormComponent, {
            formConfig: {
                name: "Select package to link boxes to",
                description: "",
                fields: [
                    {
                        label: "Package name",
                        name: "size",
                        size: "full",
                        type: "select",
                        values: this.packages
                            .filter((p: Warehouse.IPackage): boolean => p.name !== this.selected_package_name)
                            .map((p: Warehouse.IPackage): ISelectOption => ({
                                name: p.name,
                                value: p.name
                            })),
                        selected_on_top: false,
                        required: true,
                    },
                ],
            },
            noReset: true
        });

        return res.value ?? null;
    }


    public async linkBox(box: IBox, warehouse_package_name: string = null): Promise<any> {

        if (!warehouse_package_name) {
            warehouse_package_name = await this.selectPackage();
        }

        if (!warehouse_package_name) {
            return;
        }

        this.spinnerService.show();
        await this.api3Service.put(
            `${this.state.section}/boxes/${box.id}/warehouse-transactions-package`,
            {
                warehouse_package_name,
                shipment_id: this.shipment_id
            }
        );

        this.spinnerService.hide();
    }

    public async linkSelected(): Promise<any> {
        const warehouse_package_name: string = await this.selectPackage();

        if (!warehouse_package_name) {
            return;
        }

        for (const row of this.selectedRows) {
            await this.linkBox(row, warehouse_package_name);
        }

        this.toastService.show("Boxes updated", "success");
        this.tableRef.reload();
    }

    public ngOnInit(): void {
        this.prepareTable();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.tableSettings
            && changes.selected_package_name
            && changes.selected_package_name.currentValue !== changes.selected_package_name.previousValue
        ) {
            this.tableRef.reload(this.url());
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

}
